import {
  Condicion,
  FotoProyecto,
  ReporteEmitido,
  TendenciaFoto,
} from "src/app/shared/model/calidad-codigo/calidad-codigo.model";
import { CATEGORIA_EVENTO, ESTADO_CONDICION, ESTADO_GATE, estadoDe } from "../cc-estados";
import {
  SIN_DATO,
  commitCorto,
  conteoCriterios,
  entero,
  fechaCorta,
  fechaHora,
  haceCuanto,
  letra,
  nombrePeriodo,
  pct,
  textoCi,
  textoCriterios,
  textoUmbral,
  textoVersion,
  valorMetrica,
} from "../cc-formato";
import { textoConsultaPdf } from "./actualizacion-cc";
import { DatoPdf, PdfCc, emitidoTexto } from "./pdf-cc";

/** Las cifras del panel que se listan cuando el gate no las evalúa (clave, rótulo). */
const CIFRAS_PANEL: [string, string][] = [
  ["new_accepted_issues", "Aceptados en código nuevo"],
  ["accepted_issues", "Aceptados en código general"],
  ["false_positive_issues", "Falsos positivos"],
  ["violations", "Hallazgos abiertos en código general"],
  ["security_hotspots", "Hotspots por revisar"],
  ["security_hotspots_reviewed", "Hotspots revisados (código general)"],
  ["ncloc", "Líneas de código"],
  ["new_lines", "Líneas nuevas"],
  ["tests", "Pruebas unitarias"],
  ["skipped_tests", "Pruebas omitidas"],
  ["test_success_density", "Éxito de las pruebas"],
];

/**
 * El PDF del reporte de un proyecto (folio CC-…), armado SOLO con la foto
 * congelada del folio (ReporteEmitido.datos): veredicto, ficha del análisis,
 * criterios del gate con su resultado y desde cuándo, cifras del panel que el
 * gate no evalúa, tendencia de los últimos 8 análisis y aceptados; el pie dice
 * si al emitir se consultó SonarQube (foto.actualizacion). Se descarga como
 * <folio>.pdf.
 */
export function descargarPdfProyecto(reporte: ReporteEmitido): void {
  const foto = reporte.datos as FotoProyecto | null | undefined;
  if (!foto) {
    return;
  }
  const doc = new PdfCc();
  doc.encabezado(foto.proyecto?.nombre ?? reporte.proyectos?.[0] ?? "Proyecto", "Reporte del proyecto en SonarQube", [
    `Folio ${reporte.folio}`,
    emitidoTexto(foto.fecha ?? reporte.fecha, foto.emitidoPor ?? reporte.emitidoPor),
    `Quality Gate «${foto.proyecto?.gateNombre ?? "—"}»`,
  ]);
  doc.veredicto(foto.veredicto?.estado ?? "SIN_ANALISIS", textoVeredicto(foto));
  doc.titulo("Análisis evaluado");
  doc.ficha(fichaAnalisis(foto));
  doc.titulo("Criterios del Quality Gate");
  doc.tabla(
    [
      { titulo: "Criterio" },
      { titulo: "Código", ancho: 22 },
      { titulo: "Debe ser", ancho: 22 },
      { titulo: "Valor", ancho: 24, derecha: true },
      { titulo: "Resultado", ancho: 22, resultado: true },
      { titulo: "Desde", ancho: 30 },
    ],
    (foto.condiciones ?? []).map((c) => filaCondicion(c)),
  );
  const fueraDelGate = cifrasFueraDelGate(foto);
  if (fueraDelGate.length > 0) {
    doc.titulo("Cifras del panel que el gate no evalúa");
    doc.tabla([{ titulo: "Cifra" }, { titulo: "Valor", ancho: 50, derecha: true }], fueraDelGate);
  }
  doc.titulo("Tendencia (últimos 8 análisis guardados)");
  doc.tabla(
    [
      { titulo: "Fecha", ancho: 32 },
      { titulo: "Gate", ancho: 22, resultado: true },
      { titulo: "Cobertura", ancho: 26, derecha: true },
      { titulo: "Duplicación", ancho: 28, derecha: true },
      { titulo: "Hallazgos abiertos", ancho: 36, derecha: true },
      { titulo: "Commit" },
    ],
    (foto.tendencia ?? []).map((t) => filaTendencia(t)),
  );
  doc.titulo("Aceptados");
  doc.parrafo(textoAceptados(foto));
  // La clave del proyecto ya va en la ficha («Clave de SonarQube»): el pie no la repite y así cabe en dos renglones.
  // Dice además si al emitir se consultó SonarQube o salió con la última foto guardada (un folio anterior, nada).
  doc.terminar(
    {
      fuente: [
        `Fuente: SonarQube ${foto.fuente?.urlWeb ?? ""} · análisis ${foto.analisis?.analisisKey ?? SIN_DATO}.`,
        textoConsultaPdf(foto.actualizacion),
        "Datos tal como estaban al emitirse; el folio guarda la foto y se vuelve a imprimir igual.",
      ]
        .filter(Boolean)
        .join(" "),
      folio: reporte.folio,
      huella: reporte.huella,
    },
    `${reporte.folio}.pdf`,
  );
}

function textoVeredicto(foto: FotoProyecto): string {
  const v = foto.veredicto;
  if (!v || v.estado === "SIN_ANALISIS") {
    return "El proyecto no tiene análisis en SonarQube.";
  }
  const c = conteoCriterios(v.criterios);
  const partes = [`Quality Gate «${foto.proyecto?.gateNombre ?? "—"}»: ${textoCriterios(c)} criterios`];
  if (c.sinDato > 0) {
    partes.push(`${c.sinDato} sin dato`);
  }
  if (c.ignoradas > 0) {
    partes.push(`${c.ignoradas} ${c.ignoradas === 1 ? "ignorada" : "ignoradas"} por SonarQube`);
  }
  let texto = `${partes.join("; ")}.`;
  if (v.estadoDesde) {
    const verbo = v.estado === "PASA" ? "Pasa" : "No pasa";
    texto += ` ${verbo} desde el análisis del ${fechaCorta(v.estadoDesde)}${seguidosTexto(v.analisisSeguidos)}.`;
  }
  // Lo que el backend agrega a la foto y conviene que QA vea de entrada.
  if (v.desactualizado) {
    const dias = v.diasDesdeAnalisis != null ? ` (${entero(v.diasDesdeAnalisis)} días sin analizarse)` : "";
    texto += ` Análisis desactualizado${dias}.`;
  }
  if (v.sinCodigoNuevo) {
    texto += " Sin código nuevo: las cifras de código nuevo no aplican.";
  }
  if (v.gateCambio) {
    texto += " Los criterios del Quality Gate cambiaron después de este análisis.";
  }
  return texto;
}

/** "A · A · A": seguridad, confiabilidad y mantenibilidad del código general, si la foto las trae. */
function letrasTexto(foto: FotoProyecto): string {
  const c = foto.calificaciones;
  if (!c) {
    return "";
  }
  const letras = [c.seguridad, c.confiabilidad, c.mantenibilidad].map((l) => letra(l, "—"));
  return letras.every((l) => l === "—") ? "" : letras.join(" · ");
}

/** " (11 análisis seguidos)" o " (1 análisis)"; vacío sin dato. */
function seguidosTexto(n: number | null | undefined): string {
  if (n == null) {
    return "";
  }
  return n === 1 ? ` (${n} análisis)` : ` (${n} análisis seguidos)`;
}

/** "01/10/2026 13:06 (análisis fijado)": desde cuándo cuenta el código nuevo. */
function periodoTexto(foto: FotoProyecto): string {
  const periodo = foto.analisis?.periodo;
  if (!periodo?.fecha) {
    return SIN_DATO;
  }
  const tipo = periodo.tipo ? ` (${nombrePeriodo(periodo.tipo, periodo.valor)})` : "";
  return `${fechaHora(periodo.fecha)}${tipo}`;
}

/**
 * La ficha del análisis: los eventos como los congeló el servidor (ya en español; los avisos técnicos de SonarQube no
 * se imprimen, como en la pantalla); el CI y la versión como en la
 * pantalla: «no detectado» aunque una foto vieja traiga el «undetected» de SonarQube (el rótulo concuerda con ese
 * texto) y «—» en vez del «not provided» de un análisis sin versión.
 */
function fichaAnalisis(foto: FotoProyecto): DatoPdf[] {
  const a = foto.analisis ?? {};
  const periodo = periodoTexto(foto);
  const eventos = (a.eventos ?? []).map((e) => `${CATEGORIA_EVENTO[e.categoria] ?? e.categoria}: ${e.nombre}`).join(" · ");
  const letras = letrasTexto(foto);
  const datos: DatoPdf[] = [
    { rotulo: "Fecha", valor: a.fecha ? `${fechaHora(a.fecha)} (${haceCuanto(a.fecha)})` : SIN_DATO },
    { rotulo: "Commit", valor: commitCorto(a.revision) || SIN_DATO },
    { rotulo: "Versión del proyecto", valor: textoVersion(a.versionProyecto) || SIN_DATO },
    { rotulo: "Sistema de integración continua", valor: textoCi(a.ciDetectado) || SIN_DATO },
    { rotulo: "Código nuevo desde", valor: periodo },
    { rotulo: "Servidor", valor: a.versionSonar ? `SonarQube ${a.versionSonar}` : "SonarQube" },
    { rotulo: "Eventos", valor: eventos || "Ninguno" },
    { rotulo: "Clave de SonarQube", valor: foto.proyecto?.clave ?? SIN_DATO },
  ];
  if (letras) {
    datos.push({ rotulo: "Calificaciones (seguridad · confiabilidad · mantenibilidad)", valor: letras });
  }
  return datos;
}

function filaCondicion(c: Condicion): string[] {
  const estado = estadoDe(ESTADO_CONDICION, c.estado);
  // «· 1 seguidos» no se dice: con un solo análisis basta la fecha.
  const seguidos = c.analisisSeguidos != null && c.analisisSeguidos > 1 ? ` · ${entero(c.analisisSeguidos)} seguidos` : "";
  const desde = c.desde ? fechaCorta(c.desde) + seguidos : "";
  return [
    c.nombre,
    c.codigoNuevo ? "Nuevo" : "General",
    textoUmbral(c),
    c.estado === "SIN_DATO" ? "sin dato" : valorMetrica(c.metrica, c.valor),
    estado.texto,
    c.estado === "ERROR" || c.estado === "OK" ? desde : "",
  ];
}

function cifrasFueraDelGate(foto: FotoProyecto): string[][] {
  const enElGate = new Set((foto.condiciones ?? []).map((c) => c.metrica));
  const medidas = foto.medidas ?? {};
  return CIFRAS_PANEL.filter(([clave]) => !enElGate.has(clave) && medidas[clave] != null)
    .map(([clave, rotulo]) => [rotulo, valorMetrica(clave, medidas[clave])]);
}

function filaTendencia(t: TendenciaFoto): string[] {
  return [
    fechaHora(t.fecha),
    estadoDe(ESTADO_GATE, t.estado).texto,
    pct(t.cobertura ?? t.coverage),
    pct(t.duplicacion ?? t.duplicatedLinesDensity),
    entero(t.hallazgosAbiertos ?? t.violations),
    commitCorto(t.revision) || SIN_DATO,
  ];
}

function textoAceptados(foto: FotoProyecto): string {
  const a = foto.aceptados ?? {};
  const nuevos = a.nuevos ?? Number(foto.medidas?.["new_accepted_issues"]);
  const total = a.total ?? Number(foto.medidas?.["accepted_issues"]);
  return `Hallazgos aceptados en código nuevo: ${entero(Number.isFinite(nuevos) ? nuevos : null)} · en código general: ${entero(Number.isFinite(total) ? total : null)}. Quién los aceptó, cuándo y con qué comentario se consulta en SonarQube (queda fuera de este reporte).`;
}
